import {
  can,
  childCount,
  flowLane,
  laneOf,
  isSubItem,
  kindFlow,
  KINDS,
  kindLabel,
  keyLabel,
  kindProblem,
  parentProblem,
  parseKey,
  projectCode,
  structIndex,
  type BoardItem,
  type ItemKind,
  type StructIndex,
  type Viewer,
} from "@boem/core/shared";
import { useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { api, ApiError } from "../api.ts";
import { projectTone } from "../grouping.ts";
import { JumpContext } from "../util.tsx";
import { Icon } from "./Icons.tsx";
import { RowAvatars } from "./Assign.tsx";
import { ErrorLine, Modal } from "./Modal.tsx";

// Projects and sub-items on the board: the kind badge, where an item sits, what's
// under it, and the dialog that changes those. The rules are the server's own
// (shared.ts), so the dialog only offers what will be accepted.

export function KindBadge({ kind }: { kind: ItemKind | null }) {
  if (!kind) return null;
  return (
    <span className={`kind-badge k-${kind}`} title={KINDS.find((k) => k.id === kind)?.hint}>
      {kindLabel(kind)}
    </span>
  );
}

/** A draft (OEM-286): not ready for the team yet; hidden from others unless they show drafts. */
export function DraftBadge({ it }: { it: Pick<BoardItem, "draft"> }) {
  if (!it.draft) return null;
  const tip = `Draft${it.draft.by ? ` (${it.draft.by})` : ""}: not ready for the team yet. Others see it only with Show drafts, or from a link`;
  return (
    <span className="draft-badge" title={tip}>
      Draft
    </span>
  );
}

/** How an item is numbered where it's shown: in its project 01-07 (just #07 on that project's own
 *  board or panel), a project its own number, anything else its # number. */
export function itemNo(it: Pick<BoardItem, "num" | "key" | "kind" | "projectNo">, scope: number | null = null): string {
  if (it.key) return scope === it.key.projectNum ? `#${String(it.key.seq).padStart(2, "0")}` : it.key.label;
  if (it.kind === "project" && it.projectNo != null) return `Project ${projectCode(it.projectNo)}`;
  return `#${it.num}`;
}

/** The big number on a card: 07 in its project, 01-07 elsewhere, 01 for a project, 188 otherwise. */
export function cardNo(it: Pick<BoardItem, "num" | "key" | "kind" | "projectNo">, scope: number | null = null): string {
  if (it.key) return scope === it.key.projectNum ? String(it.key.seq).padStart(2, "0") : it.key.label;
  if (it.kind === "project" && it.projectNo != null) return projectCode(it.projectNo);
  return String(it.num).padStart(2, "0");
}

/** The number in small under the big one, when the big one isn't it: "#185" under 07 or 01-07, "#172" under a project's 01. */
export function cardSubNo(it: Pick<BoardItem, "num" | "key" | "kind" | "projectNo">): string | null {
  return it.key || (it.kind === "project" && it.projectNo != null) ? `#${it.num}` : null;
}

/**
 * The number column of a card (design 4.1): what you'd say out loud where the card is, the global
 * number small under it when that differs, and every number it answers to in the tooltip.
 */
export function NumCol({ it, scope = null }: { it: BoardItem; scope?: number | null }) {
  const small = cardSubNo(it);
  const inScope = !!it.key && it.key.projectNum === scope;
  const project = it.kind === "project" && it.projectNo != null;
  let big: ReactNode = cardNo(it, scope);
  if (it.key && !inScope) {
    // 01-07: the project part a little lighter, so the item's own number reads first
    const [pc, seq] = it.key.label.split("-");
    big = (
      <>
        <span className="pc">{pc}-</span>
        {seq}
      </>
    );
  } else if (!project) {
    big = (
      <>
        <span className="hash">#</span>
        {big}
      </>
    );
  }
  return (
    <div className="num" title={allNumbers(it)}>
      <span className="num-big" data-len={it.key && !inScope ? "5" : undefined}>
        {big}
      </span>
      {small && <span className="num-sub">{small}</span>}
    </div>
  );
}

/** What a move did to an item's project numbers (the server's answer to organize). */
export interface KeyMoveView {
  num: number;
  from: string | null;
  to: string | null;
  returned: boolean;
}

/** Says what happened to the numbers, for a toast: "02-08 is 03-09 now; 02-08 stays reserved for it". */
export const describeMoves = (moves: KeyMoveView[]) =>
  moves
    .map((m) =>
      m.from && m.to
        ? `${m.from} is ${m.to} now${m.returned ? " (its number there from before)" : ""}; ${m.from} stays reserved for it`
        : m.to
          ? `numbered ${m.to}`
          : `${m.from} is out of its project: it goes by #${m.num} now, and ${m.from} stays reserved for it (searching ${m.from} still finds it)`,
    )
    .join(". ");

/** Every number an item answers to, for a tooltip: "01-07 · #188 · was 02-03", "Project 01 · #172". */
export const allNumbers = (it: Pick<BoardItem, "num" | "key" | "formerKeys"> & Partial<Pick<BoardItem, "kind" | "projectNo">>) =>
  [
    it.kind === "project" && it.projectNo != null ? `Project ${projectCode(it.projectNo)}` : null,
    it.key?.label,
    `#${it.num}`,
    ...it.formerKeys.map((k) => `was ${k}`),
  ]
    .filter(Boolean)
    .join(" · ");

/** `fallback`: how to number it when the board hasn't said (a card rendered on its own) */
function Ref({ num, title, fallback }: { num: number; title: string; fallback?: string | null }) {
  const { jump, labels } = useContext(JumpContext);
  return (
    <button type="button" className="xref" title={`Go to it: ${title}`} onClick={() => jump(num)}>
      {labels?.get(num) ?? fallback ?? `#${num}`} <span className="place-title">{title}</span>
    </button>
  );
}

/** "In Project 01 Board" / "Sub-item of 01-07 Returns · in Project 01 Board" (#07 on project 01's own board).
 *  `withProject` false: the board says the project already (the card's project chip, or a Group by project
 *  heading), so the line leaves it out: none for an item right in a project, "Sub-item of 01-07 Returns" for a sub-item. */
export function PlaceLine({ it, withProject = true }: { it: BoardItem; withProject?: boolean }) {
  if (!it.parent) return null;
  const sub = it.parent.kind !== "project";
  if (!sub && !withProject) return null;
  const projectLabel = it.project?.projectNo != null ? `Project ${projectCode(it.project.projectNo)}` : null;
  return (
    <div className="place-line">
      {sub ? "Sub-item of " : "In "}
      <Ref num={it.parent.num} title={it.parent.title} fallback={sub ? it.parent.key : projectLabel} />
      {sub && withProject && it.project && (
        <>
          {" · in "}
          <Ref num={it.project.num} title={it.project.title} fallback={projectLabel} />
        </>
      )}
    </div>
  );
}

/**
 * The project an item is in, as a small chip in its card's badge row (the main board, the archive):
 * "03 Delivery app", the title ellipsised, in the project's own steady colour (one of the avatars'
 * tone sets). Clicking it shows only that project's items (the board's Project filter); pressed while
 * it does, and clicking it again shows everything.
 */
export function ProjectChip({
  project,
  on = false,
  onClick,
}: {
  project: Pick<BoardItem, "num" | "title" | "projectNo">;
  on?: boolean;
  onClick?: () => void;
}) {
  const code = project.projectNo != null ? projectCode(project.projectNo) : `#${project.num}`;
  const tip = on ? `Showing only project ${code} ${project.title}: click to show everything` : `Project ${code} ${project.title}: show only its items`;
  return (
    <button type="button" className="pchip" data-tone={projectTone(project)} aria-pressed={on} title={tip} aria-label={tip} onClick={onClick}>
      <b>{code}</b>
      <span className="pchip-t">{project.title}</span>
    </button>
  );
}

/** "; 12 archived without being done (not counted)": for a progress tooltip, when there are any. */
export const archivedNote = (it: Pick<BoardItem, "children">) => {
  const n = it.children.archived ?? 0;
  return n ? `; ${n} archived without being done (not counted)` : "";
};

/** How far along what's under it is: "3 of 5 done", with a bar. Archived items that aren't done don't count. */
export function Progress({ it }: { it: Pick<BoardItem, "children"> }) {
  const { total, done } = it.children;
  if (!total) return null;
  return (
    <span className="progress" title={`${done} of ${total} closed${archivedNote(it)}`}>
      <span className="progress-bar" aria-hidden="true">
        <span style={{ width: `${Math.round((done / total) * 100)}%` }} />
      </span>
      {done} of {total} done
    </span>
  );
}

/**
 * What's under an item, on its card (design 4.4): one row each - number, kind, title, assignee -
 * and three ways to look at it without losing your place: ▸ open it right here (its full card,
 * one level deep), ⧉ in a panel, ↗ go to it on its board. A project card lists its items the
 * same way. The head has the progress and ▸▸ to open them all here (the card's own head icons have
 * ⧉ for the item or project itself). Who has a row is a 16px avatar, its tooltip the card's. The
 * controls are 28px icon buttons (Icons.tsx); a row that can't open in place keeps an empty slot,
 * so every list lines up.
 */
export function SubItems({
  it,
  kids,
  scope = null,
  viewer,
  onPanel,
  onGoto,
  renderInline,
}: {
  it: BoardItem;
  kids: BoardItem[];
  /** who is looking: their own avatar on a row gets the accent ring */
  viewer?: Pick<Viewer, "id">;
  /** the project whose board or panel this is: its items go by #07 there, 01-07 elsewhere */
  scope?: number | null;
  /** open an item (a row) in a panel */
  onPanel?: (num: number) => void;
  /** go to an item on its board */
  onGoto?: (num: number) => void;
  /** the full card of a sub-item, shown in place; left out inside a card that is itself shown in place */
  renderInline?: (kid: BoardItem) => ReactNode;
}) {
  const { jump } = useContext(JumpContext);
  const [open, setOpen] = useState<Set<number>>(() => new Set());
  if (!it.children.total) return null;
  const project = it.kind === "project";
  const label = project ? "Items" : "Sub-items";
  const toggle = (n: number) =>
    setOpen((cur) => {
      const next = new Set(cur);
      if (next.has(n)) next.delete(n);
      else next.add(n);
      return next;
    });
  const allOpen = kids.length > 0 && kids.every((k) => open.has(k.num));
  const look = onPanel ?? jump;
  return (
    <div className="subitems" data-of={project ? "project" : "item"}>
      <div className="subitems-head">
        <span className="sub-label">{label} &middot;</span>
        <Progress it={it} />
        <span className="sub-head-acts">
          {renderInline && kids.length > 0 && (
            <button
              type="button"
              className="sub-act sub-all"
              aria-expanded={allOpen}
              aria-label={allOpen ? `Hide every ${label.toLowerCase().replace(/s$/, "")} here` : `Show every ${label.toLowerCase().replace(/s$/, "")} here, in place`}
              title={allOpen ? "Collapse all" : "Expand all in place"}
              onClick={() => setOpen(allOpen ? new Set() : new Set(kids.map((k) => k.num)))}
            >
              <Icon name="chevrons" />
            </button>
          )}
        </span>
      </div>
      {kids.length > 0 && (
        <ul>
          {kids.map((k) => {
            const no = itemNo(k, scope);
            const shown = open.has(k.num);
            const where = k.project ? `the ${k.project.title} board` : "the main board";
            return (
              <li key={k.num} data-lane={flowLane(k)} data-status={k.status} data-num={k.num} className={shown ? "sub-open" : undefined}>
                <div className="sub-row">
                  {renderInline ? (
                    <button
                      type="button"
                      className="sub-act sub-toggle"
                      aria-expanded={shown}
                      aria-label={`${shown ? "Hide" : "Show"} ${no} here`}
                      title={shown ? "Hide it" : "Show it here, in place"}
                      onClick={() => toggle(k.num)}
                    >
                      <Icon name="chevron" />
                    </button>
                  ) : (
                    <span className="sub-act sub-gap" aria-hidden="true" />
                  )}
                  <KindBadge kind={k.kind} />
                  {k.draft && <DraftBadge it={k} />}
                  <span className="sub-key" title={allNumbers(k)}>
                    {no}
                  </span>
                  <button type="button" className="xref sub-title" title={onPanel ? `Open it in a panel: ${k.title}` : `Go to it: ${k.title}`} onClick={() => look(k.num)}>
                    {k.title}
                  </button>
                  <RowAvatars it={k} viewerId={viewer?.id} />
                  <span className="sub-acts">
                    {onPanel && (
                      <button type="button" className="sub-act" aria-label={`Open ${no} in a panel`} title="Open in a panel to the right" onClick={() => onPanel(k.num)}>
                        <Icon name="panel" />
                      </button>
                    )}
                    {onGoto && (
                      <button type="button" className="sub-act" aria-label={`Go to ${no} on its board`} title={`Go to it on ${where}`} onClick={() => onGoto(k.num)}>
                        <Icon name="goto" />
                      </button>
                    )}
                  </span>
                </div>
                {shown && renderInline && <div className="sub-inline">{renderInline(k)}</div>}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/** A project's numbers, for its board: "02-01" to "02-10", and the numbers kept for items that
 *  left it ("02-08 reserved: #177 left"). Reserved numbers come from every item on the board
 *  whose former numbers include one of this project's while it is no longer in it. */
export function projectRange(
  project: Pick<BoardItem, "num" | "projectNo">,
  items: Pick<BoardItem, "num" | "key" | "formerKeys">[],
): { from: string | null; to: string | null; reserved: { key: string; num: number }[] } | null {
  const no = project.projectNo;
  if (no == null) return null;
  let max = 0;
  const reserved: { key: string; num: number; seq: number }[] = [];
  for (const it of items) {
    if (it.key?.projectNum === project.num) {
      max = Math.max(max, it.key.seq);
      continue;
    }
    for (const k of it.formerKeys) {
      const p = parseKey(k);
      if (!p || p.projectNo !== no) continue;
      reserved.push({ key: keyLabel(no, p.seq), num: it.num, seq: p.seq });
      max = Math.max(max, p.seq);
    }
  }
  reserved.sort((a, b) => a.seq - b.seq);
  return {
    from: max ? keyLabel(no, 1) : null,
    to: max ? keyLabel(no, max) : null,
    reserved: reserved.map(({ key, num }) => ({ key, num })),
  };
}

type SidebarView = "log" | "archive" | "whatsnew";

/**
 * The boards down the left: the main board, then every project (number, title, progress, OWN when
 * it has a board of its own). A row switches the board; ⧉ is for the panel. A drawer under 900px.
 */
export function ProjectSidebar({
  top,
  personal = false,
  projects,
  active,
  view = "log",
  mainOpen,
  open,
  pinned,
  onBoard,
  onPanel,
  onNewProject,
  onArchive,
  onNews,
  onClose,
  views,
  viewOpen = false,
}: {
  /** above the main board: the Boards block (the board switcher's list, orgs.md 4.2) */
  top?: ReactNode;
  /** under the Projects list: the saved and quick views (OEM-268) */
  views?: ReactNode;
  /** a view is open on the board: neither the main board nor a project is the current row */
  viewOpen?: boolean;
  /** a personal board: its numbers are its own (the footer says so) */
  personal?: boolean;
  projects: BoardItem[];
  /** projects the viewer has pinned as a panel: a small pin mark on their row */
  pinned?: Set<number>;
  /** the project whose board is showing; null = the main board */
  active: number | null;
  /** what the page shows: a board, the archive or What's new */
  view?: SidebarView;
  /** open items on the main board */
  mainOpen: number;
  /** shown as a drawer on narrow screens */
  open: boolean;
  onBoard: (num: number | null) => void;
  /** ⧉ beside a project: open it in a panel beside the board */
  onPanel: (num: number) => void;
  /** leads: a new project (New item with Project picked) */
  onNewProject?: () => void;
  onArchive: () => void;
  /** What's new: what happened on the board in a period (the meeting brief) */
  onNews?: () => void;
  onClose: () => void;
}) {
  const here = (num: number | null) => (view === "log" && !viewOpen && active === num ? "page" : undefined);
  // the drawer takes the focus when it opens, so the keyboard lands in it
  const nav = useRef<HTMLElement>(null);
  useEffect(() => {
    if (open) nav.current?.querySelector<HTMLElement>(".sb-row")?.focus();
  }, [open]);
  return (
    <>
      <nav className="sidebar" id="sidebar" ref={nav} data-open={open ? "1" : undefined} aria-label="Boards and projects">
        <button type="button" className="sb-close" aria-label="Close the list" onClick={onClose}>
          <Icon name="close" />
        </button>
        {top}
        <button type="button" className="sb-row sb-main" aria-current={here(null)} onClick={() => onBoard(null)}>
          <span className="sb-title">Main board</span>
          <span className="sb-n">{mainOpen} open</span>
        </button>
        <div className="sb-head">Projects &middot; {projects.length}</div>
        {projects.map((p) => {
          const { done, total } = p.children;
          const code = p.projectNo != null ? projectCode(p.projectNo) : `#${p.num}`;
          return (
            <div key={p.num} className="sb-proj" data-current={here(p.num) ? "1" : undefined} data-status={p.status} data-pinned={pinned?.has(p.num) ? "1" : undefined}>
              <button
                type="button"
                className="sb-row"
                aria-current={here(p.num)}
                title={`${code} ${p.title}${p.ownBoard ? " - a board of its own" : ""}${p.status === "closed" ? " (closed)" : ""}: open it as the board`}
                onClick={() => onBoard(p.num)}
              >
                <span className="sb-code">{code}</span>
                <span className="sb-title">{p.title}</span>
                <span className="sb-meta">
                  {pinned?.has(p.num) && (
                    <span className="sb-pin" title="Pinned: it opens as a panel when you sign in">
                      <Icon name="pin" className="on" />
                    </span>
                  )}
                  {p.ownBoard && (
                    <span className="sb-own" title="A board of its own: its items stay off the main board">
                      own
                    </span>
                  )}
                  <span className="sb-bar" aria-hidden="true">
                    <span style={{ width: `${total ? Math.round((done / total) * 100) : 0}%` }} />
                  </span>
                  <span className="sb-n" title={`${done} of ${total} done${archivedNote(p)}`}>
                    {done}/{total}
                  </span>
                </span>
              </button>
              <button type="button" className="sb-panel" aria-label={`Open ${p.title} in a panel`} title="Open in a panel" onClick={() => onPanel(p.num)}>
                <Icon name="panel" />
              </button>
            </div>
          );
        })}
        {!projects.length && <p className="sb-empty">No projects yet.</p>}
        {onNewProject && (
          <button type="button" className="sb-new" title="Leads: a project groups features, bugs and tasks under a number of its own" onClick={onNewProject}>
            + New project
          </button>
        )}
        {views}
        {/* pinned to the bottom, below the projects: What's new and the archive, as rows like the main board */}
        <div className="sb-bottom">
          {onNews && (
            <button type="button" className="sb-row sb-view" aria-current={view === "whatsnew" ? "page" : undefined} title="What was worked on, deployed and verified in the last few days" onClick={onNews}>
              <span className="sb-title">What&rsquo;s new</span>
            </button>
          )}
          <button type="button" className="sb-row sb-view" aria-current={view === "archive" ? "page" : undefined} onClick={onArchive}>
            <span className="sb-title">Archive</span>
          </button>
          {personal ? (
            <div className="sb-foot">
              Your board&rsquo;s own numbers: <b>01</b> is your first project, <b>01-04</b> its 4th item, <b>#2</b> the number every item here keeps. OEMUAP&rsquo;s #2 is a different item.
            </div>
          ) : (
            <div className="sb-foot">
              Numbers: <b>01</b> is the project; <b>01-07</b> its 7th item; <b>#188</b> the board-wide number every item keeps.
            </div>
          )}
        </div>
      </nav>
      {open && <div className="sb-scrim" onClick={onClose} aria-hidden="true" />}
    </>
  );
}

type Where = "none" | "project" | "sub";

/** How a dialog names an item: "03-01 · #48", "Project 03 · #180", "#188". */
export const dialogRef = (it: Pick<BoardItem, "num" | "key" | "kind" | "projectNo">) =>
  it.key ? `${it.key.label} · #${it.num}` : it.kind === "project" && it.projectNo != null ? `Project ${projectCode(it.projectNo)} · #${it.num}` : `#${it.num}`;

const KEEPS = "keeps its notes, screenshots, history and numbers. One history line.";
const Keeps = "Keeps its notes, screenshots, history and numbers. One history line.";

/**
 * What changing an item's kind does, in one line for Organize (design 4.12), or why it can't
 * (`guard`). Changing kind is one column and one history line; the only side effect is that a
 * feature or bug in the deploy or verify queue that becomes a task goes back to To do. The only
 * guarded changes are to and from Project, with the server's own words (kindProblem).
 */
export function kindPreview(
  it: Pick<BoardItem, "num" | "kind" | "status" | "stage" | "projectNo" | "children"> & Partial<Pick<BoardItem, "key" | "parent" | "project">>,
  to: ItemKind | null,
  ix: StructIndex,
  /** may make projects and un-make them (leads) */
  lead = true,
  /** the number a new project would get (the highest on the board + 1); null: not sure */
  nextNo: number | null = null,
): { text: string; guard: boolean } | null {
  if (to === it.kind) return null;
  if ((to === "project" || it.kind === "project") && !lead)
    return { text: it.kind === "project" ? "Only leads can turn a project into something else." : "Only leads can make projects.", guard: true };
  // an item under something becomes a project in one save: the server takes it out from under it first
  if (to === "project" && it.parent) {
    const sub = it.parent.kind !== "project";
    const proj = it.project?.projectNo != null ? projectCode(it.project.projectNo) : null;
    const steps = [
      sub ? `comes out from under ${it.parent.key ?? `#${it.parent.num}`}` : null,
      proj ? `leaves project ${proj}${it.key ? ` (${it.key.label} stays reserved for it)` : ""}` : null,
    ].filter(Boolean);
    const becomes =
      it.projectNo != null ? `becomes project ${projectCode(it.projectNo)} again (its number from before)` : nextNo != null ? `becomes project ${projectCode(nextNo)}` : "becomes a project with a new project number";
    const said = [...steps, becomes].join(steps.length > 1 ? ", " : " and ").replace(/, (becomes .*)$/, " and $1");
    const n = childCount(it);
    return {
      text: `${said.charAt(0).toUpperCase()}${said.slice(1)}.${n ? ` Its ${n} sub-item${n === 1 ? " comes" : "s come"} along as its item${n === 1 ? "" : "s"}.` : ""} It leaves the fix flow and ${KEEPS}`,
      guard: false,
    };
  }
  const why = kindProblem(ix, it.num, to);
  if (why) return { text: why, guard: true };
  const label = to ? kindLabel(to).toLowerCase() : null;
  const lane = laneOf(it);
  if (it.kind === "project") {
    const n = childCount(it);
    const code = it.projectNo != null ? projectCode(it.projectNo) : null;
    return {
      text:
        `Becomes a ${label ?? "plain item"}` +
        (n ? `: its ${n} item${n === 1 ? "" : "s"} become its sub-items and leave the project (their numbers there stay reserved for them)` : "") +
        (code ? `. Project number ${code} stays its own for good. ` : ". ") +
        Keeps,
      guard: false,
    };
  }
  if (to === "project") {
    const back = it.projectNo != null ? ` (${projectCode(it.projectNo)}, its number from before)` : "";
    return { text: `Becomes a project: gets its own number${back}; items can be put in it. It leaves the fix flow and ${KEEPS}`, guard: false };
  }
  if (to === "task") {
    const queue =
      lane === "deploy"
        ? "leaves the deploy queue (back to To do)"
        : lane === "verify"
          ? "leaves the verify queue (back to To do)"
          : lane === "closed"
            ? "shows as Done"
            : "stays in To do (Done closes it)";
    return { text: `Becomes a task: ${queue}, ${KEEPS}`, guard: false };
  }
  if (it.kind === "task") {
    const flow = it.status === "closed" ? "joins the fix flow (it stays closed until someone reopens it)" : "joins the fix flow at To do (developed → deployed → verified)";
    return { text: `Becomes ${label ? `a ${label}` : "a plain item"}: ${flow}. ${Keeps}`, guard: false };
  }
  if (!to) return { text: `Not set: same flow, same queue, listed under Kind "Not set". ${Keeps}`, guard: false };
  return { text: `Becomes a ${label}: same flow, same queue, ${KEEPS}`, guard: false };
}

/** Set what an item is and what it sits under; for leads, make it a project with its own board; for admins, lock it to its project. */
export function OrganizeModal({
  item,
  items,
  viewer,
  onClose,
  onSaved,
}: {
  item: BoardItem;
  items: BoardItem[];
  viewer: Viewer;
  onClose: () => void;
  /** with what the move did to its project numbers */
  onSaved: (moves: KeyMoveView[]) => void;
}) {
  const ix = useMemo(() => structIndex(items.map((i) => ({ num: i.num, kind: i.kind, parentNum: i.parent?.num ?? null }))), [items]);
  const byNum = useMemo(() => new Map(items.map((i) => [i.num, i])), [items]);
  const projects = useMemo(() => items.filter((i) => i.kind === "project" && i.num !== item.num), [items, item.num]);
  const startWhere: Where = !item.parent ? "none" : item.parent.kind === "project" ? "project" : "sub";
  const [kind, setKind] = useState<ItemKind | null>(item.kind);
  const [where, setWhere] = useState<Where>(startWhere);
  const [projectNum, setProjectNum] = useState<number | null>(item.parent?.kind === "project" ? item.parent.num : (projects[0]?.num ?? null));
  const [subOf, setSubOf] = useState(item.parent && item.parent.kind !== "project" ? String(item.parent.num) : "");
  const [ownBoard, setOwnBoard] = useState(item.ownBoard);
  const [lockOn, setLockOn] = useState(item.locked);
  const [lockedBy, setLockedBy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const lead = can(viewer, "project");
  const admin = can(viewer, "lock");

  // who locked it: the newest lock line in its history (the board doesn't carry it)
  useEffect(() => {
    if (!item.locked) return;
    let live = true;
    api.history(item.num).then(
      (rows) => {
        const last = rows.filter((r) => r.kind === "lock").sort((a, b) => b.at.localeCompare(a.at))[0];
        if (live && last?.actor) setLockedBy(last.actor);
      },
      () => {},
    );
    return () => {
      live = false;
    };
  }, [item.num, item.locked]);

  const isProject = kind === "project";
  const parent = isProject || where === "none" ? null : where === "project" ? projectNum : Number(subOf) || null;
  // a new project gets the next number after the highest on the board (the server says the last word)
  const nextNo = useMemo(() => {
    const max = items.reduce((m, i) => Math.max(m, i.projectNo ?? 0), 0);
    return max > 0 ? max + 1 : null;
  }, [items]);
  const preview = useMemo(() => kindPreview(item, kind, ix, lead, nextNo), [item, kind, ix, lead, nextNo]);
  // the same checks the server makes, on a copy: kind first (or parent first when it becomes a project)
  const problem = useMemo(() => {
    if (preview?.guard) return preview.text;
    if (!isProject && where === "sub" && !Number(subOf)) return "Give the number of the item it goes under.";
    if (!isProject && where === "project" && projectNum == null) return "Pick a project.";
    const copy = structIndex([...ix.byNum.values()].map((n) => ({ ...n })));
    const node = copy.byNum.get(item.num)!;
    const steps = isProject ? ["parent", "kind"] : ["kind", "parent"];
    for (const s of steps) {
      if (s === "kind" && kind !== node.kind) {
        const why = kindProblem(copy, item.num, kind);
        if (why) return why;
        node.kind = kind;
      }
      if (s === "parent" && parent !== node.parentNum) {
        const why = parentProblem(copy, item.num, parent);
        if (why) return why;
        node.parentNum = parent;
      }
    }
    return null;
  }, [preview, ix, item.num, kind, isProject, where, subOf, projectNum, parent]);

  // the project it is in now, and the one it would be in: undefined while that isn't known yet
  const fromProject = item.kind === "project" ? null : (item.project?.num ?? null);
  const toProject: number | null | undefined = (() => {
    if (isProject || parent == null) return null;
    const p = byNum.get(parent);
    if (!p) return undefined;
    return p.kind === "project" ? p.num : (p.project?.num ?? null);
  })();
  const movesProject = toProject !== undefined && toProject !== fromProject;
  // a lock keeps it in its project: an admin unticks it first; anyone else is told who to ask
  const lockBlocks = lockOn && movesProject && fromProject != null;

  const changes = {
    ...(kind !== item.kind ? { kind } : {}),
    ...(parent !== (item.parent?.num ?? null) ? { parent } : {}),
    ...(isProject && lead && ownBoard !== item.ownBoard ? { ownBoard } : {}),
  };
  // (becoming a project takes it out of its project, so an admin may untick the lock for that too; a project is never locked)
  const lockChange = admin && lockOn !== item.locked && (!isProject || !lockOn);
  const save = async () => {
    const organize = Object.keys(changes).length > 0;
    if (!organize && !lockChange) return onClose();
    setBusy(true);
    setError(null);
    let unlocked = false;
    try {
      // unlock before a move, lock after one: the server refuses to move a locked item
      if (lockChange && !lockOn) {
        await api.setLocked(item.num, false);
        unlocked = true;
      }
      const r = organize ? await api.organize(item.num, changes) : { moves: [] };
      if (lockChange && lockOn) await api.setLocked(item.num, true);
      onSaved(r.moves);
      onClose();
    } catch (e) {
      // the move failed: put the lock back as it was
      if (unlocked) await api.setLocked(item.num, true).catch(() => {});
      setError(e instanceof ApiError ? e.message : "Couldn't reach the server. Try again.");
    } finally {
      setBusy(false);
    }
  };
  const subNow = isSubItem(ix, { num: item.num, kind: item.kind, parentNum: item.parent?.num ?? null });
  const kids = childCount(item);
  const code = (num: number | null | undefined) => {
    const p = num != null ? byNum.get(num) : undefined;
    return p?.projectNo != null ? projectCode(p.projectNo) : num != null ? `#${num}` : "";
  };
  const titleOf = (num: number | null | undefined) => (num != null ? (byNum.get(num)?.title ?? `#${num}`) : "");

  // what the choice does to its numbers, before Save (design 4.5): the numbering box
  const numbers = ((): { tone: "info" | "warn" | "lock"; body: ReactNode } | null => {
    if (isProject) {
      if (item.kind === "project" && item.projectNo != null) {
        const c = projectCode(item.projectNo);
        return {
          tone: "info",
          body: (
            <>
              It keeps project number <b>{c}</b> for good; its items are numbered <b>{c}-01</b>, <b>{c}-02</b>, ... in the order they joined.
            </>
          ),
        };
      }
      // it leaves its project to become one: a lock keeps it there until an admin unticks it
      if (lockBlocks)
        return {
          tone: "lock",
          body: (
            <>
              {lockedBy ? <b>{lockedBy}</b> : "An admin"} locked it to project {code(fromProject)} {titleOf(fromProject)}. It can&rsquo;t leave it to become a project until an admin unlocks it
              {admin ? " (untick Locked below)" : ""}.
            </>
          ),
        };
      return {
        tone: "info",
        body:
          item.projectNo != null ? (
            <>
              It gets its project number <b>{projectCode(item.projectNo)}</b> back, and numbers the items put in it from where it left off.
            </>
          ) : nextNo != null ? (
            <>
              A project gets its own number the first time it becomes one (the next is <b>{projectCode(nextNo)}</b>) and keeps it for good. Items put in it are numbered{" "}
              <b>{projectCode(nextNo)}-01</b>, <b>-02</b>, ... in the order they join.
            </>
          ) : (
            <>A project gets its own number the first time it becomes one (the next one free) and keeps it for good. Items put in it are numbered 01, 02, ... in the order they join.</>
          ),
      };
    }
    if (toProject === undefined) return null;
    const curKey = item.key?.label ?? null;
    const withKids = kids && movesProject ? ` Its ${kids} sub-item${kids === 1 ? "" : "s"} move${kids === 1 ? "s" : ""} with it and ${kids === 1 ? "is" : "are"} numbered the same way.` : "";
    if (!movesProject) {
      if (!curKey)
        return {
          tone: "info",
          body: (
            <>
              It stays where it is and goes by <b>#{item.num}</b>.
              {item.formerKeys.length > 0 && (
                <>
                  {" "}
                  Reserved for it from before: <b>{item.formerKeys.join(", ")}</b>; searching those still finds it.
                </>
              )}
            </>
          ),
        };
      return {
        tone: "info",
        body: (
          <>
            It stays <b>{curKey}</b> in {titleOf(fromProject)}. Moving it to another project gives it that project&rsquo;s next number and keeps <b>{curKey}</b> reserved for it; out of any project it goes by <b>#{item.num}</b>.
          </>
        ),
      };
    }
    if (lockBlocks)
      return {
        tone: "lock",
        body: (
          <>
            {lockedBy ? <b>{lockedBy}</b> : "An admin"} locked it to project {code(fromProject)} {titleOf(fromProject)}. It can&rsquo;t change project until an admin unlocks it
            {admin ? " (untick Locked below)" : ""}.
          </>
        ),
      };
    if (toProject == null)
      return {
        tone: "warn",
        body: (
          <>
            It leaves {titleOf(fromProject)} and goes by <b>#{item.num}</b> again. <b>{curKey ?? ""}</b> stays reserved for it: nobody else gets it, searching it still finds this item, and it gets <b>{curKey}</b> back if it returns.{withKids}
          </>
        ),
      };
    const target = byNum.get(toProject);
    const former = target?.projectNo != null ? item.formerKeys.find((k) => parseKey(k)?.projectNo === target.projectNo) : undefined;
    const next = former ? (
      <>
        <b>{former}</b> (its number there from before)
      </>
    ) : (
      <>the next number in {titleOf(toProject)}</>
    );
    if (!curKey)
      return {
        tone: "info",
        body: (
          <>
            It gets {next}. Its board-wide number <b>#{item.num}</b> stays, and &ldquo;#{item.num}&rdquo; in notes keeps pointing here.{withKids}
          </>
        ),
      };
    return {
      tone: "warn",
      body: (
        <>
          It moves from <b>{curKey}</b> to {next}. <b>{curKey}</b> stays reserved for it in {titleOf(fromProject)}: nobody else gets it, and it is <b>{curKey}</b> again if it comes back. The move goes in its history with your name.{withKids}
        </>
      ),
    };
  })();
  // the lock: admins tick it; others see it (and Save stays off while their choice would move it)
  const showLock = isProject ? item.locked && fromProject != null : fromProject != null || (toProject != null && toProject !== undefined) || item.locked;

  return (
    <Modal onClose={onClose}>
      <h3>Organize {dialogRef(item)}</h3>
      <p>{item.title}</p>
      <label>Change kind</label>
      <div className="asg-chips" role="radiogroup" aria-label="Change kind">
        {KINDS.filter((k) => k.id !== "project" || lead || item.kind === "project").map((k) => (
          <button key={k.id} type="button" className="chip" role="radio" aria-checked={kind === k.id} aria-pressed={kind === k.id} title={k.hint} onClick={() => setKind(k.id)}>
            {k.label}
          </button>
        ))}
        <button type="button" className="chip" role="radio" aria-checked={kind === null} aria-pressed={kind === null} onClick={() => setKind(null)}>
          Not set
        </button>
      </div>
      <div className={`kind-prev${preview?.guard ? " guard" : ""}`} role={preview?.guard ? "alert" : undefined}>
        {preview ? preview.text : `Stays ${item.kind ? `a ${kindLabel(item.kind).toLowerCase()}` : "a plain item"} (${kindFlow(item.kind) === "fix" ? "the fix flow" : kindFlow(item.kind) === "done" ? "To do → Done" : "no flow"}). Pick another kind to see what would change.`}
      </div>

      {isProject ? (
        <>
          <div className="hint" style={{ marginTop: 10 }}>
            Projects are always at the top. Features, bugs and tasks go in them, and those can have sub-items. An item keeps its #{item.num} for good.
          </div>
          {lead && (
            <label className="tm-check" style={{ marginTop: 12 }}>
              <input type="checkbox" checked={ownBoard} onChange={(e) => setOwnBoard(e.target.checked)} /> Board of its own: keep its items off the main board (marked OWN in the
              sidebar)
            </label>
          )}
        </>
      ) : (
        <>
          <label style={{ marginTop: 14 }}>Where it sits</label>
          <div className="place-opts">
            <label className="tm-check">
              <input type="radio" name="where" checked={where === "none"} onChange={() => setWhere("none")} /> On its own
            </label>
            <label className="tm-check">
              <input type="radio" name="where" checked={where === "project"} disabled={!projects.length} onChange={() => setWhere("project")} /> In a project
              {projects.length > 0 && (
                <select
                  className="search"
                  value={projectNum ?? ""}
                  aria-label="Project"
                  onChange={(e) => {
                    setProjectNum(Number(e.target.value));
                    setWhere("project");
                  }}
                >
                  {projects.map((p) => (
                    <option key={p.num} value={p.num}>
                      {p.projectNo != null ? projectCode(p.projectNo) : `#${p.num}`} {p.title}
                    </option>
                  ))}
                </select>
              )}
              {!projects.length && <span className="hint-inline">no projects yet</span>}
            </label>
            <label className="tm-check">
              <input type="radio" name="where" checked={where === "sub"} onChange={() => setWhere("sub")} /> A sub-item of #
              <input
                type="text"
                inputMode="numeric"
                className="search num-input"
                value={subOf}
                placeholder="142"
                aria-label="Item number it goes under"
                onChange={(e) => {
                  setSubOf(e.target.value.replace(/\D/g, "").slice(0, 6));
                  setWhere("sub");
                }}
              />
            </label>
          </div>
          {subNow && where !== "sub" && (
            <div className="hint">It stops being a sub-item of {item.parent!.key ?? `#${item.parent!.num}`}.</div>
          )}
        </>
      )}
      {numbers && <div className={`numbox${numbers.tone === "info" ? "" : ` ${numbers.tone}`}`}>{numbers.body}</div>}
      {showLock &&
        (admin ? (
          <label className="tm-check lock-check">
            <input type="checkbox" checked={lockOn} onChange={(e) => setLockOn(e.target.checked)} /> Locked to its project (admins): nobody can move it to another project
            {item.locked && lockedBy ? ` · locked by ${lockedBy}` : ""}
          </label>
        ) : (
          item.locked && <div className="hint lock-hint">Locked to its project{lockedBy ? ` by ${lockedBy}` : ""}. Only an admin can unlock it.</div>
        ))}
      {problem && !preview?.guard && <div className="msg err">{problem}</div>}
      {lockBlocks && !admin && <div className="msg err">Locked: ask an admin to unlock it first.</div>}
      <div className="row">
        <button className="btn" onClick={onClose}>
          Cancel
        </button>
        <button className="btn primary" disabled={busy || !!problem || lockBlocks} onClick={save}>
          Save
        </button>
      </div>
      <ErrorLine error={error} />
    </Modal>
  );
}
